import { inject, Injectable } from '@angular/core';
import { BaseService } from '@shared/core';
import 'leaflet';
import 'leaflet.markercluster';
import { IParkingLocation, MapLayers, MapProviderTypeEnum } from '../models/parking-map';
import { MarkersService } from './markers.service';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const L = window['L'];

declare module 'leaflet' {
  interface MarkerOptions {
    parkingId?: string;
  }
}

@Injectable()
export class MapService extends BaseService {
  private map!: L.Map;
  private currentTileLayer?: L.TileLayer;
  private markerService = inject(MarkersService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private markerClusterGroup!: any;
  private parkings: IParkingLocation[] = [];

  private mapLayers = MapLayers;

  initMap(center: [number, number], zoom: number): void {
    this.map = L.map('map', {
      center,
      zoom,
      attributionControl: false,
    });
    this.setMapLayer(MapProviderTypeEnum.GIS2);

    this.markerClusterGroup = L.markerClusterGroup({
      maxClusterRadius: 80,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      chunkedLoading: true,
      disableClusteringAtZoom: 18,
      animate: true,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      iconCreateFunction: cluster => {
        const markers = cluster.getAllChildMarkers();
        let totalSpots = 0;
        let freeSpots = 0;

        markers.forEach((marker: L.Marker) => {
          const parkingId = (marker.options as L.MarkerOptions).parkingId;
          const parking = this.parkings.find(p => p.id === parkingId);
          if (parking) {
            totalSpots += parking.totalSpots ?? 0;
            freeSpots += parking.freeSpotsCount ?? 0;
          }
        });

        const percentage = totalSpots ? ((totalSpots - freeSpots) / totalSpots) * 100 : 0;
        const color = this.markerService.getMarkerColor(percentage, totalSpots, freeSpots);

        return L.divIcon({
          html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; text-align: center; line-height: 30px; color: white; font-weight: bold;">${markers.length}</div>`,
          className: 'marker-cluster',
          iconSize: [30, 30],
        });
      },
    });

    this.markerClusterGroup.on('click', (e: L.LeafletMouseEvent) => {
      const marker = e.layer as L.Marker;
      const parkingId = (marker.options as L.MarkerOptions).parkingId;

      const popup = marker.getPopup();
      if (popup && popup.isOpen()) {
        marker.closePopup();
        return;
      }

      this.markerService.closeAllPopups();

      if (parkingId) {
        this.get(`parkings/positioning/${parkingId}`).subscribe({
          next: response => {
            if (response.value) {
              marker
                .bindPopup(this.markerService.createPopupContent(response.value), {
                  closeButton: false,
                  closeOnClick: false,
                  autoClose: false,
                })
                .openPopup();
            }
          },
          error: () => {
            marker.bindPopup('Не удалось загрузить данные парковки').openPopup();
          },
        });
      }
    });

    this.map.on('click', () => {
      this.markerService.closeAllPopups();
    });

    this.map.addLayer(this.markerClusterGroup);
  }

  setMapLayer(type: MapProviderTypeEnum, isGrayscale = false): void {
    if (this.currentTileLayer) {
      this.map.removeLayer(this.currentTileLayer);
    }

    const layer = this.mapLayers[type];
    const options = {
      ...layer.options,
      className: isGrayscale ? 'grayscale-darken' : '',
    };

    this.currentTileLayer = L.tileLayer(layer.url, options).addTo(this.map);
  }

  clearMarkers(): void {
    this.markerClusterGroup?.clearLayers();
    this.markerService?.clearMarkers();
  }

  addMarkers(parkings: IParkingLocation[]): void {
    this.parkings = parkings;
    this.markerService.setParkings(parkings);
    parkings.forEach(parking => {
      if (parking.id && parking.location && parking.location.x && parking.location.y) {
        const marker = this.markerService.createMarker(parking);
        this.markerClusterGroup.addLayer(marker);
      }
    });
  }

  updateParkingOccupancy(parkingId: string, freeSpotsCount: number): void {
    this.markerService.updateParkingOccupancy(parkingId, freeSpotsCount);
  }

  filterMarkers(parkingIds: string[]): void {
    if (!this.markerClusterGroup) {
      return;
    }

    this.markerClusterGroup.clearLayers();

    if (!parkingIds.length) {
      return;
    }

    const filteredParkings = this.parkings.filter(
      parking => parking.id && parkingIds.includes(parking.id),
    );

    filteredParkings.forEach(parking => {
      if (parking.id && parking.location) {
        const marker = this.markerService.createMarker(parking);
        this.markerClusterGroup.addLayer(marker);
      }
    });

    this.map.invalidateSize();
  }

  refreshMap(): void {
    if (this.map) {
      this.map.invalidateSize();
    }
  }

  get mapInstance(): L.Map {
    return this.map;
  }
}
